import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import logo from "../../assets/UpdatedMainLogo.png";
import getApiBase from "../../components/getApiBase";

// Header component (matching AdminDashboard theme)
const Header = ({ onLogout }) => {
  const navigate = useNavigate();

  return (
    <header
      style={{
        backgroundColor: "#fff",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
        padding: "10px 20px",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <img src={logo} alt="Autoshops.com Logo" style={{ height: "auto", width: "300px" }} />
        </Box>
        <nav style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            onClick={() => navigate("/admin")}
            sx={{
              color: "#ea4335",
              fontWeight: 700,
              fontSize: "18px",
              "&:hover": { backgroundColor: "#e63926", color: "#fff" },
            }}
          >
            Dashboard
          </Button>
          <Button
            onClick={onLogout}
            sx={{
              color: "#ea4335",
              fontWeight: 700,
              fontSize: "18px",
              "&:hover": { backgroundColor: "#e63926", color: "#fff" },
            }}
          >
            Logout
          </Button>
        </nav>
      </Box>
    </header>
  );
};

const AdminPricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("filter") || "active"); // all, active, inactive - default to "active"
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    monthly_price: "",
    yearly_price: "",
    plan_type: "addon",
    plan_type_display_name: "",
    plan_type_order: "",
    max_active_bids: "",
    features: "",
    is_active: true,
    display_order: 0,
  });

  useEffect(() => {
    checkAuth();
    fetchPlans();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${getApiBase()}/api/auth/me`, {
        credentials: "include",
      });

      if (!response.ok) {
        navigate("/admin");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      navigate("/admin");
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${getApiBase()}/api/admin/subscription-plans`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error("Failed to load plans:", response.status, errorData);
        if (response.status === 403) {
          const roleInfo = errorData.yourRoles ? ` (Your role: ${errorData.yourRoles})` : '';
          setError(`Permission denied: You need 'manage_billing' permission to access subscription plans.${roleInfo} Please contact a super administrator to grant you this permission.`);
        } else {
          setError(`Failed to load plans: ${errorData.error || 'Unknown error'}`);
        }
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      setError(`Failed to load plans: ${err.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/admin");
  };

  const handleAddClick = () => {
    setSelectedPlan(null);
    setFormData({
      name: "",
      display_name: "",
      description: "",
      monthly_price: "",
      yearly_price: "",
      plan_type: "addon",
      plan_type_display_name: "",
      plan_type_order: "",
      max_active_bids: "",
      features: "",
      is_active: true,
      display_order: 0,
    });
    setAddModalOpen(true);
  };

  const handleEditClick = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      name: plan.name || "",
      display_name: plan.display_name,
      description: plan.description || "",
      monthly_price: plan.monthly_price,
      yearly_price: plan.yearly_price || "",
      plan_type: plan.plan_type || "addon",
      plan_type_display_name: plan.plan_type_display_name || "",
      plan_type_order: plan.plan_type_order || "",
      max_active_bids: plan.max_active_bids || "",
      features: Array.isArray(plan.features) ? plan.features.join("\n") : "",
      is_active: plan.is_active === 1,
      display_order: plan.display_order || 0,
    });
    setEditModalOpen(true);
  };

  const handleSavePlan = async () => {
    try {
      // Validation
      if (!formData.plan_type_display_name || !formData.plan_type_order) {
        alert("Please fill in required fields: Plan Type Display Name and Plan Type Order");
        return;
      }

      const featuresArray = formData.features
        .split("\n")
        .map((f) => f.trim())
        .filter((f) => f.length > 0);

      const payload = {
        ...formData,
        features: featuresArray,
        monthly_price: parseFloat(formData.monthly_price),
        yearly_price: formData.yearly_price ? parseFloat(formData.yearly_price) : null,
        max_active_bids: formData.max_active_bids ? parseInt(formData.max_active_bids) : null,
        plan_type_display_name: formData.plan_type_display_name ? formData.plan_type_display_name.trim() : null,
        plan_type_order: formData.plan_type_order ? parseInt(formData.plan_type_order) : 0,
        is_active: formData.is_active ? 1 : 0,
      };

      const response = await fetch(
        `${getApiBase()}/api/admin/subscription-plans/${selectedPlan.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setEditModalOpen(false);
        fetchPlans();
      } else {
        alert("Failed to update plan");
      }
    } catch (err) {
      console.error("Error saving plan:", err);
      alert("Failed to save plan");
    }
  };

  const handleCreatePlan = async () => {
    try {
      // Validation
      if (!formData.name || !formData.display_name || !formData.monthly_price || !formData.plan_type_display_name || !formData.plan_type_order) {
        alert("Please fill in required fields: Name, Display Name, Monthly Price, Plan Type Display Name, and Plan Type Order");
        return;
      }

      const featuresArray = formData.features
        .split("\n")
        .map((f) => f.trim())
        .filter((f) => f.length > 0);

      const payload = {
        ...formData,
        features: featuresArray,
        monthly_price: parseFloat(formData.monthly_price),
        yearly_price: formData.yearly_price ? parseFloat(formData.yearly_price) : null,
        max_active_bids: formData.max_active_bids ? parseInt(formData.max_active_bids) : null,
        display_order: formData.display_order ? parseInt(formData.display_order) : 0,
        plan_type_display_name: formData.plan_type_display_name ? formData.plan_type_display_name.trim() : null,
        plan_type_order: formData.plan_type_order ? parseInt(formData.plan_type_order) : 0,
        is_active: formData.is_active ? 1 : 0,
      };

      const response = await fetch(
        `${getApiBase()}/api/admin/subscription-plans`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setAddModalOpen(false);
        fetchPlans();
      } else {
        const error = await response.json();
        alert(`Failed to create plan: ${error.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error("Error creating plan:", err);
      alert("Failed to create plan");
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/subscription-plans/${plan.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            is_active: plan.is_active === 1 ? 0 : 1,
          }),
        }
      );

      if (response.ok) {
        fetchPlans();
      }
    } catch (err) {
      console.error("Error toggling plan:", err);
    }
  };

  const getPlanTypeColor = (type) => {
    switch (type) {
      case "bid_tier":
        return "primary";
      case "addon":
        return "success";
      case "base_tier":
        return "warning";
      case "system_feature":
        return "info";
      default:
        return "default";
    }
  };

  const getPlanTypeLabel = (type) => {
    switch (type) {
      case "bid_tier":
        return "Bid Tier";
      case "addon":
        return "Add-On";
      case "base_tier":
        return "Base Tier";
      case "system_feature":
        return "System Feature";
      default:
        return type;
    }
  };

  // Filter plans by status
  const getFilteredPlans = () => {
    if (statusFilter === "active") {
      return plans.filter((p) => p.is_active === 1);
    } else if (statusFilter === "inactive") {
      return plans.filter((p) => p.is_active === 0 || !p.is_active);
    }
    return plans; // all
  };

  const filteredPlans = getFilteredPlans();

  // Group filtered plans dynamically by plan_type
  const planGroups = filteredPlans.reduce((groups, plan) => {
    const type = plan.plan_type || 'other';
    if (!groups[type]) {
      groups[type] = {
        plans: [],
        displayName: plan.plan_type_display_name || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        order: plan.plan_type_order || 99
      };
    }
    groups[type].plans.push(plan);
    return groups;
  }, {});

  // Convert to array and sort by plan_type_order
  const sortedGroups = Object.entries(planGroups)
    .map(([type, data]) => ({ type, ...data }))
    .sort((a, b) => a.order - b.order);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header onLogout={handleLogout} />

      <Box sx={{ p: 4 }}>
        {/* Page Header */}
        <Box
          sx={{
            backgroundColor: "#e63926",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#fff" }}>
            💰 Subscription Plans & Pricing
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl sx={{ minWidth: 150, backgroundColor: "#fff", borderRadius: 1 }} size="small">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{
                  "& .MuiSelect-select": {
                    py: 1
                  }
                }}
              >
                <MenuItem value="all">All Plans</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#4285f4",
                "&:hover": { backgroundColor: "#1564c0" },
              }}
              onClick={handleAddClick}
            >
              + Add New Plan
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* Dynamically render all plan type groups */}
            {sortedGroups.map((group) => group.plans.length > 0 && (
              <Box mb={4} key={group.type}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: "#e63926",
                    mb: 2,
                    pb: 1,
                    borderBottom: "2px solid #e63926",
                  }}
                >
                  {group.displayName}
                </Typography>
                <Grid container spacing={3}>
                  {group.plans.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map((plan) => (
                    <Grid item xs={12} md={6} lg={4} key={plan.id}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 3,
                          height: "100%",
                          opacity: plan.is_active === 0 ? 0.6 : 1,
                          border: plan.is_active === 0 ? "2px dashed #ccc" : "none",
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                          <Box>
                            <Typography variant="h6" fontWeight="bold">
                              {plan.display_name}
                            </Typography>
                            <Chip
                              label={getPlanTypeLabel(plan.plan_type)}
                              color={getPlanTypeColor(plan.plan_type)}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          </Box>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(plan)}
                              sx={{ color: "#4285f4" }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" mb={2}>
                          {plan.description}
                        </Typography>

                        <Box mb={2}>
                          <Typography variant="h5" color="primary" fontWeight="bold">
                            ${parseFloat(plan.monthly_price).toFixed(2)}
                            <Typography variant="body2" component="span" color="text.secondary">
                              /month
                            </Typography>
                          </Typography>
                          {plan.yearly_price && (
                            <Typography variant="body2" color="text.secondary">
                              ${parseFloat(plan.yearly_price).toFixed(2)}/year
                            </Typography>
                          )}
                        </Box>

                        {plan.max_active_bids && (
                          <Typography variant="body2" fontWeight="medium" mb={1}>
                            Max Bids: {plan.max_active_bids}/day
                          </Typography>
                        )}

                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                          <Chip
                            label={plan.is_active === 1 ? "Active" : "Inactive"}
                            color={plan.is_active === 1 ? "success" : "default"}
                            size="small"
                          />
                          <Button
                            size="small"
                            onClick={() => handleToggleActive(plan)}
                            sx={{ color: "#e63926" }}
                          >
                            {plan.is_active === 1 ? "Deactivate" : "Activate"}
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* Edit Plan Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#e63926", color: "#fff", fontWeight: "bold" }}>
          Edit Plan: {selectedPlan?.display_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monthly Price ($)"
                type="number"
                value={formData.monthly_price}
                onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Yearly Price ($) - Optional"
                type="number"
                value={formData.yearly_price}
                onChange={(e) => setFormData({ ...formData, yearly_price: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Plan Type</InputLabel>
                <Select
                  value={formData.plan_type}
                  label="Plan Type"
                  onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                >
                  <MenuItem value="bid_tier">Bid Tier</MenuItem>
                  <MenuItem value="addon">Add-On</MenuItem>
                  <MenuItem value="base_tier">Base Tier</MenuItem>
                  <MenuItem value="system_feature">System Feature</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plan Type Display Name *"
                name="planTypeDisplayName"
                value={formData.plan_type_display_name || ''}
                onChange={(e) => setFormData({ ...formData, plan_type_display_name: e.target.value })}
                required
                margin="normal"
                helperText="Display name for this plan type (e.g., 'System Feature', 'Subscription Plan', 'Add-On')"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plan Type Order *"
                name="planTypeOrder"
                type="number"
                value={formData.plan_type_order || ''}
                onChange={(e) => setFormData({ ...formData, plan_type_order: e.target.value })}
                required
                margin="normal"
                helperText="Sort order number (lower numbers appear first)"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Bids Per Day (for bid tiers)"
                type="number"
                value={formData.max_active_bids}
                onChange={(e) => setFormData({ ...formData, max_active_bids: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features (one per line)"
                multiline
                rows={6}
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                helperText="Enter each feature on a new line"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSavePlan}
            variant="contained"
            sx={{
              backgroundColor: "#4285f4",
              "&:hover": { backgroundColor: "#1564c0" },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Plan Modal */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#4285f4", color: "#fff", fontWeight: "bold" }}>
          Create New Plan
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name (unique identifier)*"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                helperText="e.g., addon_custom_feature"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Name*"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monthly Price ($)*"
                type="number"
                value={formData.monthly_price}
                onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Yearly Price ($)"
                type="number"
                value={formData.yearly_price}
                onChange={(e) => setFormData({ ...formData, yearly_price: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Plan Type</InputLabel>
                <Select
                  value={formData.plan_type}
                  label="Plan Type"
                  onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                >
                  <MenuItem value="bid_tier">Bid Tier</MenuItem>
                  <MenuItem value="addon">Add-On</MenuItem>
                  <MenuItem value="base_tier">Base Tier</MenuItem>
                  <MenuItem value="system_feature">System Feature</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Plan Type Display Name *"
                name="planTypeDisplayName"
                value={formData.plan_type_display_name || ''}
                onChange={(e) => setFormData({ ...formData, plan_type_display_name: e.target.value })}
                required
                margin="normal"
                helperText="Display name for this plan type (e.g., 'System Feature', 'Subscription Plan', 'Add-On')"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Plan Type Order *"
                name="planTypeOrder"
                type="number"
                value={formData.plan_type_order || ''}
                onChange={(e) => setFormData({ ...formData, plan_type_order: e.target.value })}
                required
                margin="normal"
                helperText="Sort order number (lower numbers appear first)"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Max Bids Per Day"
                type="number"
                value={formData.max_active_bids}
                onChange={(e) => setFormData({ ...formData, max_active_bids: e.target.value })}
                helperText="For bid tiers only"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                helperText="Sorting order"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features (one per line)"
                multiline
                rows={6}
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                helperText="Enter each feature on a new line"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active (visible to users)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleCreatePlan}
            variant="contained"
            sx={{
              backgroundColor: "#4285f4",
              "&:hover": { backgroundColor: "#1564c0" },
            }}
          >
            Create Plan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPricing;
